import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Upload,
  Image,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  disableRefetch,
  removeFromMenu,
  setMenuData,
} from '../../redux/slices/menu';
import { fetchMerchants } from '../../redux/slices/merchant';
import merchantService from '../../services/merchant';
import userService from '../../services/user';
import { useTranslation } from 'react-i18next';
import TextArea from 'antd/es/input/TextArea';
import { DebounceSelect } from '../../components/search';

const MerchantAdd = () => {
  const { t } = useTranslation();
  const { activeMenu } = useSelector((state) => state.menu, shallowEqual);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { uuid } = useParams();

  // Image states
  const [crnImage, setCrnImage] = useState({ file: null, preview: null });
  const [taxCardImage, setTaxCardImage] = useState({ file: null, preview: null });
  const [licenseImage, setLicenseImage] = useState({ file: null, preview: null });
  const [pharmacyFrontImage, setPharmacyFrontImage] = useState({ file: null, preview: null });

  const [loadingBtn, setLoadingBtn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState({ visible: false, src: '', title: '' });

  useEffect(() => {
    return () => {
      const data = form.getFieldsValue(true);
      dispatch(setMenuData({ activeMenu, data }));
    };
  }, []);

  async function fetchUserList(search) {
    const params = { search, 'roles[0]': 'user' };
    return userService.search(params).then((res) =>
      res?.data?.map((item) => ({
        label: `${item?.firstname ?? ''} ${item?.lastname ?? ''}`.trim() || item?.email,
        value: item?.id,
      })) ?? []
    );
  }

  const getMerchant = (uuid) => {
    setLoading(true);
    merchantService
      .getById(uuid)
      .then(({ data }) => {
        form.setFieldsValue({
          name: data?.name ?? '',
          phone: data?.phone ?? '',
          address: data?.address ?? '',
          status: data?.status ?? 'new',
          status_note: data?.status_note ?? '',
          pharmacy_code: data?.pharmacy_code ?? '',
          crn: data?.crn ?? '',
          tax_card_number: data?.tax_card_number ?? '',
          license_number: data?.license_number ?? '',
          governorate: data?.governorate ?? '',
          area: data?.area ?? '',
          landline_number: data?.landline_number ?? '',
          whatsapp_number: data?.whatsapp_number ?? '',
          pharmacist_name: data?.pharmacist_name ?? '',
          user: data?.user
            ? {
                label: `${data?.user?.firstname ?? ''} ${data?.user?.lastname ?? ''}`.trim() || data?.user?.email,
                value: data?.user?.id,
              }
            : null,
        });
        // Set image previews from existing data
        if (data?.crn_image) {
          setCrnImage({ file: null, preview: data?.crn_image });
        }
        if (data?.tax_card_image) {
          setTaxCardImage({ file: null, preview: data?.tax_card_image });
        }
        if (data?.license_image) {
          setLicenseImage({ file: null, preview: data?.license_image });
        }
        if (data?.pharmacy_front_image) {
          setPharmacyFrontImage({ file: null, preview: data?.pharmacy_front_image });
        }
      })
      .finally(() => {
        setLoading(false);
        dispatch(disableRefetch(activeMenu));
      });
  };

  const onFinish = (values) => {
    setLoadingBtn(true);

    const formData = new FormData();
    formData.append('name', values?.name ?? '');
    formData.append('phone', values?.phone ?? '');
    formData.append('address', values?.address ?? '');
    formData.append('user_id', values?.user?.value ?? '');
    formData.append('pharmacy_code', values?.pharmacy_code ?? '');
    formData.append('crn', values?.crn ?? '');
    formData.append('tax_card_number', values?.tax_card_number ?? '');
    formData.append('license_number', values?.license_number ?? '');
    formData.append('governorate', values?.governorate ?? '');
    formData.append('area', values?.area ?? '');
    formData.append('landline_number', values?.landline_number ?? '');
    formData.append('whatsapp_number', values?.whatsapp_number ?? '');
    formData.append('pharmacist_name', values?.pharmacist_name ?? '');

    // Include status and status_note in edit mode
    if (uuid) {
      formData.append('status', values?.status ?? '');
      formData.append('status_note', values?.status_note ?? '');
    }

    // Append image files
    if (crnImage?.file) {
      formData.append('crn_image', crnImage.file);
    }
    if (taxCardImage?.file) {
      formData.append('tax_card_image', taxCardImage.file);
    }
    if (licenseImage?.file) {
      formData.append('license_image', licenseImage.file);
    }
    if (pharmacyFrontImage?.file) {
      formData.append('pharmacy_front_image', pharmacyFrontImage.file);
    }

    // For PUT requests via POST (Laravel method spoofing)
    if (uuid) {
      formData.append('_method', 'PUT');
    }

    const nextUrl = 'merchants';

    if (!uuid) {
      merchantService
        .create(formData)
        .then(() => {
          toast.success(t('successfully.created'), { autoClose: 10000 });
          dispatch(removeFromMenu({ ...activeMenu, nextUrl }));
          navigate(`/${nextUrl}`);
          dispatch(fetchMerchants());
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || t('error.occurred'), {
            autoClose: 10000,
          });
        })
        .finally(() => setLoadingBtn(false));
    } else {
      merchantService
        .update(uuid, formData)
        .then(() => {
          toast.success(t('successfully.updated'), { autoClose: 10000 });
          dispatch(removeFromMenu({ ...activeMenu, nextUrl }));
          navigate(`/${nextUrl}`);
          dispatch(fetchMerchants());
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || t('error.occurred'), {
            autoClose: 10000,
          });
        })
        .finally(() => setLoadingBtn(false));
    }
  };

  useEffect(() => {
    if (activeMenu?.refetch && uuid) {
      getMerchant(uuid);
    }
  }, [activeMenu?.refetch]);

  const handleCancel = () => {
    const nextUrl = 'merchants';
    dispatch(removeFromMenu({ ...activeMenu, nextUrl }));
    navigate(`/${nextUrl}`);
  };

  const handleImageChange = (info, setImageState) => {
    const file = info?.file?.originFileObj || info?.file;
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageState({ file, preview: e?.target?.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageRemove = (setImageState) => {
    setImageState({ file: null, preview: null });
  };

  const handleViewImage = (src, title) => {
    setPreviewImage({ visible: true, src, title });
  };

  const renderImageUpload = (label, name, imageState, setImageState, required = true) => {
    const uploadButton = (
      <div>
        <PlusOutlined />
        <div style={{ marginTop: 8 }}>{t('upload')}</div>
      </div>
    );

    return (
      <Form.Item
        label={t(label)}
        name={name}
        rules={[
          {
            required: required && !uuid && !imageState?.preview,
            message: t('required'),
          },
        ]}
      >
        <Upload
          name={name}
          listType='picture-card'
          showUploadList={false}
          beforeUpload={() => false}
          onChange={(info) => handleImageChange(info, setImageState)}
          accept='image/*'
        >
          {imageState?.preview ? (
            <img
              src={imageState.preview}
              alt={label}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            uploadButton
          )}
        </Upload>
        {imageState?.preview && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button
              type='link'
              onClick={() => handleViewImage(imageState.preview, t(label))}
              style={{ padding: 0 }}
            >
              {t('View')}
            </Button>
            <Button
              type='link'
              danger
              onClick={() => handleImageRemove(setImageState)}
              style={{ padding: 0 }}
            >
              {t('remove')}
            </Button>
          </div>
        )}
      </Form.Item>
    );
  };

  return (
    <Card
      title={uuid ? t('edit.merchant') : t('add.merchant')}
      loading={loading}
    >
      {!loading ? (
        <Form
          name='merchant-form'
          layout='vertical'
          onFinish={onFinish}
          form={form}
          initialValues={{ status: 'new', ...activeMenu?.data }}
        >
          <Row gutter={12}>
            {/* Basic Information */}
            <Col span={12}>
              <Form.Item
                label={t('seller')}
                name='user'
                rules={[{ required: true, message: t('required') }]}
              >
                <DebounceSelect fetchOptions={fetchUserList} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('name')}
                name='name'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('pharmacy.code')}
                name='pharmacy_code'
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('pharmacist.name')}
                name='pharmacist_name'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('crn')}
                name='crn'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('tax.card.number')}
                name='tax_card_number'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('license.number')}
                name='license_number'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('governorate')}
                name='governorate'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('area')}
                name='area'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('address')}
                name='address'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            {/* Contact Information */}
            <Col span={12}>
              <Form.Item
                label={t('phone')}
                name='phone'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('landline.number')}
                name='landline_number'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('whatsapp.number')}
                name='whatsapp_number'
                rules={[{ required: true, message: t('required') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            {/* Image Uploads */}
            <Col span={24}>
              <Card title={t('documents')} className='mt-3'>
                <Row gutter={12}>
                  <Col span={6}>
                    {renderImageUpload('crn.image', 'crn_image', crnImage, setCrnImage)}
                  </Col>
                  <Col span={6}>
                    {renderImageUpload('tax.card.image', 'tax_card_image', taxCardImage, setTaxCardImage)}
                  </Col>
                  <Col span={6}>
                    {renderImageUpload('license.image', 'license_image', licenseImage, setLicenseImage)}
                  </Col>
                  <Col span={6}>
                    {renderImageUpload('pharmacy.front.image', 'pharmacy_front_image', pharmacyFrontImage, setPharmacyFrontImage)}
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* Status fields (only in edit mode) */}
            {uuid && (
              <>
                <Col span={12} className='mt-3'>
                  <Form.Item name='status' label={t('status')}>
                    <Select>
                      <Select.Option value='new'>{t('new')}</Select.Option>
                      <Select.Option value='approved'>{t('approved')}</Select.Option>
                      <Select.Option value='rejected'>{t('rejected')}</Select.Option>
                      <Select.Option value='cancelled'>{t('cancelled')}</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12} className='mt-3'>
                  <Form.Item label={t('status.note')} name='status_note'>
                    <TextArea rows={2} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          <div className='d-flex justify-content-end mt-3'>
            <Button type='primary' htmlType='submit' loading={loadingBtn}>
              {t('save')}
            </Button>
            <Button className='ml-2' onClick={handleCancel}>
              {t('cancel')}
            </Button>
          </div>
        </Form>
      ) : (
        <div className='d-flex justify-content-center align-items-center'>
          <Spin size='large' className='py-5' />
        </div>
      )}

      {/* Image Preview Modal */}
      <Image
        style={{ display: 'none' }}
        src={previewImage.src}
        preview={{
          visible: previewImage.visible,
          title: previewImage.title,
          onVisibleChange: (visible) => setPreviewImage({ ...previewImage, visible }),
        }}
      />
    </Card>
  );
};

export default MerchantAdd;
